import { Parser } from '@expr-eval/js';
import { BaseError, Result } from '@inrev/core';
import { z } from 'zod';
import { Currency } from '../currency/currency.mjs';

class FormulaParseError extends BaseError {
    constructor(code, message, variables) {
        super(message);
        this.code = code;
        this.message = message;
        this.variables = variables;
        this.name = "FormulaParseError";
        this.statusCode = 400;
    }
}
const applyFormulaBrand = (schema) => schema.brand();
const MAX_FORMULA_LENGTH = 200;
const NUMERIC_LITERAL = /\d+(\.\d+)?([eE][+-]?\d+)?/g;
const IDENTIFIER = /[A-Za-z_]\w*/g;
const ALLOWED_IDENTIFIER = "g";
const SIGNED_NUMERIC_LITERAL = /^[+-]?\s*\d+(\.\d+)?([eE][+-]?\d+)?$/;
const INSTRUCTION_PREFIX = /^([dr])\s*=\s*/;
const parser = new Parser({
    allowMemberAccess: false,
    operators: {
        power: false,
        remainder: false,
        factorial: false,
        comparison: false,
        conditional: false,
        concatenate: false,
        logical: false,
        in: false,
        assignment: false,
        fndef: false,
    },
});
function splitTopLevel(input) {
    const parts = [];
    let depth = 0;
    let start = 0;
    for (let i = 0; i < input.length; i++) {
        const c = input[i];
        if (c === "(")
            depth++;
        else if (c === ")") {
            depth--;
            if (depth < 0) {
                return Result.err(new FormulaParseError("INVALID_SYNTAX", "Unbalanced parentheses in formula"));
            }
        }
        else if (c === "&" && depth === 0) {
            parts.push(input.slice(start, i));
            start = i + 1;
        }
    }
    if (depth !== 0) {
        return Result.err(new FormulaParseError("INVALID_SYNTAX", "Unbalanced parentheses in formula"));
    }
    parts.push(input.slice(start));
    return Result.ok(parts);
}
function buildTermList(trimmed) {
    return splitTopLevel(trimmed).flatMap((parts) => {
        const terms = [];
        const seen = new Set();
        for (const rawPart of parts) {
            const part = rawPart.trim();
            if (part.length === 0) {
                return Result.err(new FormulaParseError("INVALID_SYNTAX", "Empty instruction in formula"));
            }
            const match = INSTRUCTION_PREFIX.exec(part);
            const kind = match === null ? "line" : match[1] === "d" ? "dropped" : "removed";
            const inner = match === null ? part : part.slice(match[0].length).trim();
            if (inner.length === 0) {
                return Result.err(new FormulaParseError("INVALID_SYNTAX", "Missing expression after instruction prefix"));
            }
            if (seen.has(kind)) {
                const label = kind === "line" ? "bare" : kind;
                return Result.err(new FormulaParseError("INVALID_SYNTAX", `Duplicate ${label} instruction in formula`));
            }
            seen.add(kind);
            terms.push({ kind, inner });
        }
        return Result.ok(terms);
    });
}
function validateInner(inner) {
    const trimmed = inner.trim();
    if (trimmed.length === 0) {
        return new FormulaParseError("INVALID_SYNTAX", "Empty sub-expression");
    }
    const numericsStripped = trimmed.replace(NUMERIC_LITERAL, "");
    const identifiers = numericsStripped.match(IDENTIFIER) ?? [];
    const disallowed = identifiers.filter((id) => id !== ALLOWED_IDENTIFIER);
    if (disallowed.length > 0) {
        return new FormulaParseError("IDENTIFIERS_NOT_ALLOWED", `Formula may only use \`g\` as an identifier; disallowed: ${disallowed.join(", ")}`, disallowed);
    }
    try {
        parser.parse(trimmed);
    }
    catch {
        return new FormulaParseError("INVALID_SYNTAX", "Formula is not valid arithmetic");
    }
    return null;
}
function parseTerms(trimmed) {
    return buildTermList(trimmed).flatMap((terms) => {
        for (const term of terms) {
            const error = validateInner(term.inner);
            if (error)
                return Result.err(error);
        }
        return Result.ok(terms);
    });
}
const formatTerm = (term) => term.kind === "dropped"
    ? `d= ${term.inner}`
    : term.kind === "removed"
        ? `r= ${term.inner}`
        : term.inner;
const formatFormulaTerms = (terms) => terms.map(formatTerm).join(" & ");
const formulaFromTerms = (terms) => formatFormulaTerms(terms);
function literalFormula(value) {
    if (!Number.isFinite(value)) {
        throw new FormulaParseError("INVALID_SYNTAX", "Literal formula must be a finite number");
    }
    return String(value);
}
function prepareRaw(raw) {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
        return Result.err(new FormulaParseError("EMPTY", "Formula cannot be empty"));
    }
    if (trimmed.length > MAX_FORMULA_LENGTH) {
        return Result.err(new FormulaParseError("TOO_LONG", `Formula exceeds ${MAX_FORMULA_LENGTH} characters`));
    }
    return Result.ok(trimmed);
}
function parseFormulaTerms(raw) {
    return prepareRaw(raw).flatMap(parseTerms);
}
function parseFormula(raw) {
    return parseFormulaTerms(raw).map(formulaFromTerms);
}
const _formulaSchema = z
    .string({ error: "Formula cannot be empty" })
    .trim()
    .min(1, "Formula cannot be empty")
    .max(MAX_FORMULA_LENGTH, `Formula exceeds ${MAX_FORMULA_LENGTH} characters`)
    .refine((raw) => parseFormula(raw).match(() => true, () => false), "Invalid formula");
const formulaSchema = applyFormulaBrand(_formulaSchema);
function evaluateInner(given, inner) {
    try {
        const result = parser.parse(inner).evaluate({ g: given });
        return typeof result === "number" && Number.isFinite(result) ? result : Number.NaN;
    }
    catch {
        return Number.NaN;
    }
}
function evaluateFormula(given, formula) {
    return formulaContributions(given, formula).lineDelta;
}
function formulaContributions(given, formula) {
    const nan = { lineDelta: Number.NaN, droppedDelta: Number.NaN, removedDelta: Number.NaN };
    if (!Number.isFinite(given))
        return nan;
    return buildTermList(formula).match((terms) => {
        let lineDelta = Currency.ZERO;
        let droppedDelta = Currency.ZERO;
        let removedDelta = Currency.ZERO;
        for (const term of terms) {
            const rawValue = evaluateInner(given, term.inner);
            if (!Number.isFinite(rawValue))
                return nan;
            const value = Currency.round(rawValue);
            lineDelta = Currency.add(lineDelta, value);
            if (term.kind === "dropped")
                droppedDelta = Currency.add(droppedDelta, value);
            else if (term.kind === "removed")
                removedDelta = Currency.add(removedDelta, value);
        }
        if (!Number.isFinite(lineDelta))
            return nan;
        return { lineDelta, droppedDelta, removedDelta };
    }, () => nan);
}
function isLiteralFormula(formula) {
    if (!formula)
        return true;
    return SIGNED_NUMERIC_LITERAL.test(formula.trim());
}
var Formula;
(function (Formula) {
    Formula.parse = parseFormula;
    Formula.literal = literalFormula;
    Formula.evaluate = evaluateFormula;
    Formula.contributions = formulaContributions;
    Formula.isLiteral = isLiteralFormula;
})(Formula || (Formula = {}));

export { Formula, FormulaParseError, applyFormulaBrand, evaluateFormula, formulaContributions, formulaFromTerms, formulaSchema, isLiteralFormula, literalFormula, parseFormula, parseFormulaTerms };
